import styled from 'styled-components';
import { Form, Formik } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import { TextareaFormik } from 'src/components/common/Textarea';
import { makeTransition } from 'src/utils/motion/transitions';
import { useTranslate } from 'react-t7e';
// import { FormikPageLeaveBlocker } from 'src/components/common/PageLeaveBlocker';
import { Button } from 'src/components/common/Button';
import { AnimationControllerImperativeAPI } from 'src/components/common/AnimationController';
import { useRef } from 'react';
import { withAnimationController } from 'src/components/common/Button/withAnimationController';
import { Notes } from 'src/graphql/schema.graphql';
import { useMaterialsCreateUserNoteMutation } from 'src/modules/materials/hooks/useMaterialsCreateUserNoteMutation';
import { NeauviaItem } from 'src/models/directus';
import { useMaterialsUpdateUserNoteMutation } from 'src/modules/materials/hooks/useMaterialsUpdateUserNoteMutation';

export interface UserNoteFormProps {
  item: NeauviaItem;
  note?: Notes;
  onCancel?: () => void;
  onSubmit?: () => void;
}

const ActionsContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 24px;
  padding-top: 16px;
`;

const SubmitButton = styled(Button)`
  && {
    min-width: 200px;
  }
`;

export const UserNoteForm = ({
  item,
  note,
  onSubmit,
  onCancel,
}: UserNoteFormProps) => {
  const { _ } = useTranslate();
  const animationControllerRef =
    useRef<AnimationControllerImperativeAPI | null>(null);

  const isEditMode = !!note;

  const create = useMaterialsCreateUserNoteMutation(item);
  const update = useMaterialsUpdateUserNoteMutation(item);

  return (
    <Formik
      initialValues={{ content: note?.content ?? '' }}
      onSubmit={withAnimationController(
        animationControllerRef,
        async (values, formik) => {
          if (isEditMode) {
            await update.mutateAsync({ id: note.id, content: values.content });
          } else {
            await create.mutateAsync({ content: values.content });
          }
          onSubmit?.();
          formik.resetForm();
        },
      )}
    >
      {({ dirty, resetForm }) => (
        <Form
          as={motion.div}
          {...makeTransition({ fade: 'enter' })}
          layout="position"
        >
          {/* <FormikPageLeaveBlocker /> */}
          <TextareaFormik
            name="content"
            placeholder={_(isEditMode ? 'Edit a note' : 'Write a note')}
          />
          <AnimatePresence>
            {(dirty || isEditMode) && (
              <ActionsContainer
                layout="position"
                {...makeTransition({
                  fade: true,
                  from: 'top',
                  sortZIndex: true,
                })}
                key="actions"
              >
                <Button
                  kind="text"
                  onClick={() => {
                    onCancel?.();
                    resetForm();
                  }}
                  type="button"
                >
                  {_('Cancel')}
                </Button>
                <SubmitButton
                  type="submit"
                  variant="secondary"
                  stateControllerRef={animationControllerRef}
                >
                  {_(isEditMode ? 'Save' : 'Add note')}
                </SubmitButton>
              </ActionsContainer>
            )}
          </AnimatePresence>
        </Form>
      )}
    </Formik>
  );
};
